import { useState } from "react";
import { Link } from "react-router-dom";
import { cx } from "class-variance-authority";
import { useData } from "@/stores/data";
import {
  LayoutDashboard,
  Database,
  Package,
  Download,
} from "lucide-react";
import SidebarChevron from "@/assets/icons/SidebarChevron";

const adminMenuGeneral = [
  { label: "Admin", path: "/admin/admin/all" },
  { label: "Entity", path: "/admin/entity/all" },
  { label: "Region", path: "/admin/region/all" },
  { label: "Service Base", path: "/admin/service-base/all" },
  { label: "Branch", path: "/admin/branch/all" },
  { label: "Client", path: "/admin/client/all" },
  { label: "Customer", path: "/admin/customer/all" },
  { label: "Customer Type", path: "/admin/customer-type/all" },
  { label: "Contract", path: "/admin/contract/all" },
  { label: "Priority", path: "/admin/priority/all" },
  { label: "Setting", path: "/admin/setting/all" },
  { label: "Post Status Fail", path: "/admin/post-status-fail/all" },
];

const adminMenuService = [
  { label: "Service Engineer", path: "/admin/service_engineer/all" },
  {
    label: "First Level Maintenance",
    path: "/admin/first_level_maintenance/all",
  },
  { label: "Cash In Transit", path: "/admin/cash_in_transit/all" },
  { label: "Comm Vendor", path: "/admin/comm_vendor/all" },
  { label: "Team Leader", path: "/admin/team-leader/all" },
  { label: "Manager", path: "/admin/manager/all" },
  { label: "Service Desk", path: "/admin/service_desk/all" },
  {
    label: "Remote Operation Center",
    path: "/admin/remote_operation_center/all",
  },
  {
    label: "Repair Center Engineer",
    path: "/admin/repair_center_engineer/all",
  },
  { label: "Mapping Area", path: "/admin/mapping_area/all" },
  { label: "Problem Type", path: "/admin/problem-type/all" },
  { label: "Problem Type Group", path: "/admin/problem_type_group/all" },
  { label: "Service Type", path: "/admin/service_type/all" },
  { label: "Corrective Type", path: "/admin/corrective_type/all" },
  { label: "Cancel Status", path: "/admin/cancel_status/all" },
  { label: "Suspend Status", path: "/admin/suspend_status/all" },
  { label: "Reject Status", path: "/admin/reject_status/all" },
  { label: "Reason Status", path: "/admin/reason_status/all" },
  { label: "Machine", path: "/admin/machine/all" },
  { label: "Machine Model", path: "/admin/machine_model/all" },
  { label: "Machine Category", path: "/admin/machine_type/all" },
  { label: "Machine Vendor", path: "/admin/machine_vendor/all" },
  { label: "Asset Platform", path: "/admin/asset_platform/all" },
  { label: "Asset Detail", path: "/admin/asset_detail/all" },
];

const adminMenuLogistic = [
  { label: "Logistic Staff", path: "/admin/logistic_staff/all" },
  { label: "Warehouse Base", path: "/admin/warehouse_base/all" },
  { label: "Part Repairable", path: "/admin/part_repairable/all" },
  { label: "Part Non-Repairable", path: "/admin/part_non_repairable/all" },
  { label: "Part Detail", path: "/admin/part_detail/all" },
  { label: "Part Platform", path: "/admin/part_platform/all" },
  { label: "Bin Location", path: "/admin/bin_location/all" },
];

export default function SidebarAdmin({ sidebarMinMax = false }) {
  const { setCurrentImport } = useData();
  const [masterData, setMasterData] = useState(false);
  const [masterDataService, setMasterDataService] = useState(false);
  const [masterDataLogistic, setMasterDataLogistic] = useState(false);
  const [importData, setImportData] = useState(false);

  // Helper function to handle accordion behavior - close all other groups when one is opened
  const handleGroupToggle = (groupSetter, currentValue) => {
    // Close all groups
    setImportData(false);
    setMasterData(false);
    setMasterDataService(false);
    setMasterDataLogistic(false);
    // Toggle the target group
    groupSetter(!currentValue);
  };

  return (
    <>
      {/* Dashboard */}
      <Link
        to={"/admin/dashboard"}
        className={cx(
          "flex",
          "items-center",
          "gap-3",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-blue-50",
          "hover:text-blue-700",
          "transition-all",
          "duration-200",
          "group",
          "whitespace-nowrap",
          sidebarMinMax && "justify-center",
        )}
      >
        <LayoutDashboard className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
        {!sidebarMinMax && (
          <span className="font-medium whitespace-nowrap">Dashboard</span>
        )}
      </Link>

      {/* Masterdata General */}
      <div className="space-y-1">
        <button
          className={cx(
            "w-full",
            "flex",
            "items-center",
            "justify-between",
            "px-4",
            "py-3",
            "rounded-lg",
            "text-gray-700",
            "hover:bg-gray-100",
            "transition-all",
            "duration-200",
            "group",
          )}
          onClick={() => {
            handleGroupToggle(setMasterData, masterData);
          }}
        >
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            {!sidebarMinMax && (
              <span className="font-medium whitespace-nowrap">
                Masterdata General
              </span>
            )}
          </div>
          {!sidebarMinMax && (
            <SidebarChevron
              className={`transition-transform duration-200 ${
                masterData ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
        {masterData && !sidebarMinMax && (
          <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
            {adminMenuGeneral
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((menu) => (
                <Link
                  to={menu.path}
                  key={menu.label}
                  className={cx(
                    "block",
                    "px-3",
                    "py-2",
                    "text-sm",
                    "text-gray-600",
                    "hover:text-blue-600",
                    "hover:bg-blue-50",
                    "rounded-md",
                    "transition-all",
                    "duration-200",
                    "whitespace-nowrap",
                  )}
                >
                  {menu.label}
                </Link>
              ))}
          </div>
        )}
      </div>

      {/* Masterdata Service */}
      <div className="space-y-1">
        <button
          className={cx(
            "w-full",
            "flex",
            "items-center",
            "justify-between",
            "px-4",
            "py-3",
            "rounded-lg",
            "text-gray-700",
            "hover:bg-gray-100",
            "transition-all",
            "duration-200",
            "group",
          )}
          onClick={() => {
            handleGroupToggle(setMasterDataService, masterDataService);
          }}
        >
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            {!sidebarMinMax && (
              <span className="font-medium whitespace-nowrap">
                Masterdata Service
              </span>
            )}
          </div>
          {!sidebarMinMax && (
            <SidebarChevron
              className={`transition-transform duration-200 ${
                masterDataService ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
        {masterDataService && !sidebarMinMax && (
          <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
            {adminMenuService
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((menu) => (
                <Link
                  to={menu.path}
                  key={menu.label}
                  className={cx(
                    "block",
                    "px-3",
                    "py-2",
                    "text-sm",
                    "text-gray-600",
                    "hover:text-blue-600",
                    "hover:bg-blue-50",
                    "rounded-md",
                    "transition-all",
                    "duration-200",
                    "whitespace-nowrap",
                  )}
                >
                  {menu.label}
                </Link>
              ))}
          </div>
        )}
      </div>

      {/* Masterdata Logistic */}
      <div className="space-y-1">
        <button
          className={cx(
            "w-full",
            "flex",
            "items-center",
            "justify-between",
            "px-4",
            "py-3",
            "rounded-lg",
            "text-gray-700",
            "hover:bg-gray-100",
            "transition-all",
            "duration-200",
            "group",
          )}
          onClick={() => {
            handleGroupToggle(setMasterDataLogistic, masterDataLogistic);
          }}
        >
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            {!sidebarMinMax && (
              <span className="font-medium whitespace-nowrap">
                Masterdata Logistic
              </span>
            )}
          </div>
          {!sidebarMinMax && (
            <SidebarChevron
              className={`transition-transform duration-200 ${
                masterDataLogistic ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
        {masterDataLogistic && !sidebarMinMax && (
          <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
            {adminMenuLogistic
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((menu) => (
                <Link
                  to={menu.path}
                  key={menu.label}
                  className={cx(
                    "block",
                    "px-3",
                    "py-2",
                    "text-sm",
                    "text-gray-600",
                    "hover:text-blue-600",
                    "hover:bg-blue-50",
                    "rounded-md",
                    "transition-all",
                    "duration-200",
                    "whitespace-nowrap",
                  )}
                >
                  {menu.label}
                </Link>
              ))}
          </div>
        )}
      </div>

      {/* Import Data */}
      <div className="space-y-1">
        <button
          className={cx(
            "w-full",
            "flex",
            "items-center",
            "justify-between",
            "px-4",
            "py-3",
            "rounded-lg",
            "text-gray-700",
            "hover:bg-gray-100",
            "transition-all",
            "duration-200",
            "group",
          )}
          onClick={() => {
            handleGroupToggle(setImportData, importData);
          }}
        >
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            {!sidebarMinMax && (
              <span className="font-medium whitespace-nowrap">
                Import Data
              </span>
            )}
          </div>
          {!sidebarMinMax && (
            <SidebarChevron
              className={`transition-transform duration-200 ${
                importData ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
        {importData && !sidebarMinMax && (
          <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
            <button
              onClick={() => setCurrentImport("manager")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Manager
            </button>
            <button
              onClick={() => setCurrentImport("team_leader")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Team Leader
            </button>
            <button
              onClick={() => setCurrentImport("first_level_maintenance")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              First Level Maintenance
            </button>
            <button
              onClick={() => setCurrentImport("cash_in_transit")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Cash In Transit
            </button>
            <button
              onClick={() => setCurrentImport("comm_vendor")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Comm Vendor
            </button>
            <button
              onClick={() => setCurrentImport("remote_operation_center")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Remote Operation Center
            </button>
            <button
              onClick={() => setCurrentImport("service_engineer")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Service Engineer
            </button>
            <button
              onClick={() => setCurrentImport("machine")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Machine
            </button>
            <button
              onClick={() => setCurrentImport("mapping_area")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Mapping Area
            </button>
            <button
              onClick={() => setCurrentImport("asset")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Asset
            </button>
            <button
              onClick={() => setCurrentImport("part_repairable")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Part Repairable
            </button>
            <button
              onClick={() => setCurrentImport("part_non_repairable")}
              className={cx(
                "block",
                "w-full",
                "text-left",
                "px-3",
                "py-2",
                "text-sm",
                "text-gray-600",
                "hover:text-blue-600",
                "hover:bg-blue-50",
                "rounded-md",
                "transition-all",
                "duration-200",
                "whitespace-nowrap",
              )}
            >
              Part Non Repairable
            </button>
          </div>
        )}
      </div>
    </>
  );
}
